import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { X, AlertCircle, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DialogOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface DialogState {
  isOpen: boolean;
  type: 'alert' | 'confirm';
  message: string;
  title: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (value: boolean) => void;
}

interface DialogContextValue {
  showAlert: (message: string, opts?: DialogOptions) => Promise<void>;
  showConfirm: (message: string, opts?: DialogOptions) => Promise<boolean>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const open = (
    type: 'alert' | 'confirm',
    message: string,
    opts: DialogOptions = {}
  ): Promise<boolean> =>
    new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({
        isOpen: true,
        type,
        message,
        title: opts.title ?? (type === 'confirm' ? 'Confirm' : 'Notice'),
        confirmLabel: opts.confirmLabel ?? (type === 'confirm' ? 'Confirm' : 'OK'),
        danger: opts.danger ?? false,
        resolve,
      });
    });

  const handleClose = (value: boolean) => {
    resolveRef.current?.(value);
    setDialog(null);
  };

  useEffect(() => {
    if (!dialog?.isOpen) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(false); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [dialog?.isOpen]);

  const value: DialogContextValue = {
    showAlert: (msg, opts) => open('alert', msg, opts).then(() => undefined),
    showConfirm: (msg, opts) => open('confirm', msg, opts),
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      {dialog?.isOpen && (
        <div
          ref={overlayRef}
          className="modal-backdrop-v2"
          onClick={(e) => { if (e.target === overlayRef.current) handleClose(false); }}
        >
          <div
            className={`modal-v2 sm ${dialog.danger ? 'danger' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-v2-hd">
              <div className="icon-cap">
                {dialog.type === 'confirm'
                  ? <HelpCircle size={18} />
                  : <AlertCircle size={18} />
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {dialog.danger && <div className="tag">{t('appDialog.destructiveAction', 'Ação destrutiva')}</div>}
                <h2>{dialog.title}</h2>
                <div className="sub">{dialog.message}</div>
              </div>
              <button className="close" onClick={() => handleClose(false)} aria-label={t('common.close')}>
                <X size={14} />
              </button>
            </div>

            <div className="modal-v2-ft">
              <div className="spacer" />
              {dialog.type === 'confirm' && (
                <button className="btn-s ghost" onClick={() => handleClose(false)}>
                  {t('common.cancel')}
                </button>
              )}
              <button
                className={`btn-s ${dialog.danger ? 'danger' : 'primary'}`}
                onClick={() => handleClose(true)}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextValue => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within DialogProvider');
  return ctx;
};
